var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var WordFrequencyConfig_1;
/* eslint-disable class-methods-use-this */
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult, ToolValidationState } from './validation/toolValidationResult';
import { validateSourceWithDetail } from './validation/validateHelper';
import { wordFrequencyConfigSchemaValidation } from '../validations.generated';
export var WordFrequencyMatchType;
(function (WordFrequencyMatchType) {
    WordFrequencyMatchType["EXACT"] = "exact";
    // FUZZY_MATCH = 'fuzzy_match'
})(WordFrequencyMatchType || (WordFrequencyMatchType = {}));
const DEFAULT_OUTPUT_COLUMN_NAME = 'Frequency count';
export const wordFrequencyConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:WordFrequencyConfig',
    type: 'object',
    properties: {
        matchType: { type: 'string', enum: Object.values(WordFrequencyMatchType) },
        sourceColumn: { type: 'string', minLength: 1, maxLength: 150 },
        taxonomyColumn: { type: 'string', minLength: 1, maxLength: 150 },
        outputColumnName: { type: 'string', minLength: 1, maxLength: 150 },
        caseInsensitive: { type: 'boolean' }
    },
    required: ['matchType', 'sourceColumn', 'taxonomyColumn']
};
let WordFrequencyConfig = WordFrequencyConfig_1 = class WordFrequencyConfig extends ToolConfig {
    constructor(matchType, sourceColumn, taxonomyColumn, outputColumnName, caseInsensitive, customFormats) {
        super(ToolType.WORD_FREQUENCY, customFormats);
        this.matchType = matchType;
        this.sourceColumn = sourceColumn;
        this.taxonomyColumn = taxonomyColumn;
        this.outputColumnName = outputColumnName;
        this.caseInsensitive = caseInsensitive;
        this.customFormats = customFormats;
        /**
         * Default behaviour:
         * - Use exact match
         * - Case sensitive
         * - Output column name defaults to 'Occurrences'
         */
        this.outputColumnName = outputColumnName ?? DEFAULT_OUTPUT_COLUMN_NAME;
        this.caseInsensitive = caseInsensitive ?? false;
        JSON.validate(this, WordFrequencyConfig_1);
    }
    get left() {
        return WordFrequencyConfig_1.left;
    }
    get right() {
        return WordFrequencyConfig_1.right;
    }
    get out() {
        return WordFrequencyConfig_1.out;
    }
    get ins() {
        return WordFrequencyConfig_1.ins;
    }
    get outs() {
        return WordFrequencyConfig_1.outs;
    }
    static get left() {
        return WordFrequencyConfig_1.ins[0];
    }
    static get right() {
        return WordFrequencyConfig_1.ins[1];
    }
    static get out() {
        return WordFrequencyConfig_1.outs[0];
    }
    static get ins() {
        return ['left', 'right'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.WORD_FREQUENCY.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.CALCULATE], this.ins, this.outs, ['word count', 'word frequency', 'occurrences'], true // Available in Spark
        );
    }
    getOutput(iConfigHelper, ins) {
        const toolScopeBuilder = new ToolScopeBuilder(ins);
        const exports = toolScopeBuilder.export(this.out);
        const rightSchema = ins.schemas.at(this.right);
        // We copy all columns from the right input to the output
        // Plus the occurrences column
        const dependents = this.copyAllInsAndReturnDependents(exports, this.right, rightSchema, [
            this.taxonomyColumn
        ]);
        const outputColumn = new Column(this.outputColumnName ?? DEFAULT_OUTPUT_COLUMN_NAME, 'number');
        exports.makeNew(outputColumn, dependents, true);
        exports.initializeColumnFormats().setColumnFormats(this.customFormats);
        return toolScopeBuilder.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const left = scope.schemas.at(this.left);
        const right = scope.schemas.at(this.right);
        if (!left || !right) {
            return ToolValidationResult.getPortMissingEdgesResult('One or more input nodes are missing.', 'The word frequency tool requires two input nodes.');
        }
        const sourceInputs = new Map();
        const taxonomyInputs = new Map();
        sourceInputs.set(this.sourceColumn, ['string']);
        taxonomyInputs.set(this.taxonomyColumn, ['string']);
        const leftSourceValidationResult = validateSourceWithDetail(left, sourceInputs, []);
        if (!leftSourceValidationResult.isValid) {
            return ToolValidationResult.getResultFromSourceValidation(leftSourceValidationResult);
        }
        const rightSourceValidationResult = validateSourceWithDetail(right, taxonomyInputs, [
            this.outputColumnName ?? DEFAULT_OUTPUT_COLUMN_NAME
        ]);
        if (!rightSourceValidationResult.isValid) {
            return ToolValidationResult.getResultFromSourceValidation(rightSourceValidationResult);
        }
        return ToolValidationResult.getValidResult();
    }
    isValidForRun(iConfigHelper, scope, pass) {
        return pass ? ToolValidationState.BAD_METADATA : this.isValidForCalc(iConfigHelper, scope);
    }
};
WordFrequencyConfig = WordFrequencyConfig_1 = __decorate([
    JsonSchemaV2(wordFrequencyConfigSchema, wordFrequencyConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.WORD_FREQUENCY)
], WordFrequencyConfig);
export { WordFrequencyConfig };
//# sourceMappingURL=wordFrequencyConfig.js.map